/**
 * The paged, date-ranged past-runs table for one sync.
 *
 * The retention window is enforced in DATA, not just in whatever date picker calls `setRange` —
 * `clampPastRunsRange` is the one place a caller-supplied range gets pulled back inside what the
 * platform can actually answer for. Retention is PER-CONNECTION (`retentionDays()` in
 * `lib/run-view.ts` reads it off the sync's own config); 30 is only the documented default a
 * connection falls back to when it does not say, and `RUN_WINDOW_DAYS` in
 * `src/rpc/procedures/activations.ts` keeps that same 30 as the server's own query default — not a
 * platform-wide ceiling either.
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

import { useOrpc } from '@/_shared/hooks/useOrpc';

import { isRunning } from '../lib/run-view';
import type { RawSyncRun } from '../types';
import { BASE_POLL_INTERVAL_MS, SETTLED_POLL_INTERVAL_MS } from './useRunSync';

// LOCAL calendar days, not UTC ones. The boundaries here are the same boundaries the date picker
// renders and bounds itself by (`PastRunsRangePicker` uses local `dayjs()`), and a picker showing
// dates means the reader's dates. Computing in UTC and displaying in local drifts them apart: at
// UTC+3 an end-of-day UTC bound renders as TOMORROW, so a window labelled "retained for 30 days"
// reads as spanning 31. The instant sent to the platform is still absolute — `toISOString()` carries
// the offset — so retention is unaffected; only which day a reader sees is.

export const PAST_RUNS_PAGE_SIZE = 10;

export interface PastRunsRange {
  from: Date;
  to: Date;
}

/**
 * The full retention window: start-of-day (UTC) `retentionDays - 1` days back → END of today.
 *
 * The upper bound is end-of-day, not `now`, and that is load-bearing rather than tidy. The range
 * lands in component state at mount and becomes part of the query key, so a `to` of "now" freezes
 * the window at the instant the screen opened — a run triggered from this very page then falls
 * outside it, and no amount of Refresh can show it. Observed in a browser: trigger a run, press
 * Refresh, watch nothing happen while the API happily reports the new run.
 *
 * The same trap survives one step further out, and is NOT solved here: a reader who narrows the
 * picker to a window that excludes today gets the identical outcome for the identical reason — their
 * own new run lands outside the window they asked for, so the table never shows it and the
 * follow-your-own-run rule never fires. The only feedback is the header button's own state. That is
 * accepted rather than fixed: widening someone's chosen window on their behalf would be a louder
 * surprise than the silence. Stated so the next reader does not conclude the narrowed case is safe
 * merely because the default one was handled.
 *
 * End-of-day is also what the UI already claims: the picker renders dates ("Jul 13 – Aug 11 2026"),
 * not instants, so a reader has every right to expect today's runs to be in "today".
 *
 * `retentionDays` is REQUIRED, not defaulted here — `retentionDays(sync)` (`lib/run-view.ts`) is the
 * one place a missing value legitimately becomes the platform default, and every caller of THIS
 * function already has that resolved number by the time it calls in. A default here would let a
 * future caller silently ask for a 30-day window on a connection that keeps less (or more).
 */
export function defaultPastRunsRange(retentionDays: number, now: Date = new Date()): PastRunsRange {
  const today = dayjs(now);
  const from = today.startOf('day').subtract(retentionDays - 1, 'day');
  return { from: from.toDate(), to: today.endOf('day').toDate() };
}

/**
 * Never earlier than start-of-day (now − (retentionDays - 1)), never later than the end of today.
 * `retentionDays` is REQUIRED — see `defaultPastRunsRange`'s doc for why a default here is the wrong
 * place for it.
 */
export function clampPastRunsRange(range: PastRunsRange, retentionDays: number, now: Date = new Date()): PastRunsRange {
  const nowDay = dayjs(now);
  const earliest = nowDay.startOf('day').subtract(retentionDays - 1, 'day');
  const latest = nowDay.endOf('day');
  const from = dayjs(range.from).isBefore(earliest) ? earliest : dayjs(range.from);
  const to = dayjs(range.to).isAfter(latest) ? latest : dayjs(range.to);
  return { from: from.toDate(), to: to.toDate() };
}

/**
 * Milliseconds from `now` until the next local midnight — the instant `defaultPastRunsRange` starts
 * returning a different window. Always >= 1, so a timer built from it can never spin at 0.
 */
export function msUntilNextLocalMidnight(now: Date = new Date()): number {
  return Math.max(1, dayjs(now).endOf('day').valueOf() + 1 - now.getTime());
}

/**
 * True when `range` is the default window for a day that has already ended.
 *
 * Compared on the BOUNDS rather than a stored "the user has not touched it" flag. A reader who picks
 * exactly today's default window through the picker has chosen what the default would have given
 * them, and there is no reading under which they then want it frozen to yesterday. Conversely any
 * window they deliberately narrowed is not default-shaped, so it is left alone — rolling THAT would
 * throw away the thing they asked for.
 */
export function pastRunsRangeIsStale(range: PastRunsRange, retentionDays: number, now: Date = new Date()): boolean {
  const defaultForItsOwnDay = defaultPastRunsRange(retentionDays, range.to);
  const isDefaultShaped =
    range.from.getTime() === defaultForItsOwnDay.from.getTime() &&
    range.to.getTime() === defaultForItsOwnDay.to.getTime();
  return isDefaultShaped && dayjs(range.to).isBefore(dayjs(now).endOf('day'));
}

export interface PastRuns {
  runs: RawSyncRun[];
  totalRuns: number;
  /** 1-based. */
  page: number;
  pageCount: number;
  range: PastRunsRange;
  isLoading: boolean;
  isFetching: boolean;
  /**
   * A fetch the READER asked for is in flight — a page turn or a range change — as opposed to a
   * background poll.
   *
   * `isPlaceholderData`, not `isFetching`: with `keepPreviousData` set, a changed page or range means
   * a new query key, and TanStack serves the previous page's rows as placeholder until the new key
   * resolves. A `refetchInterval` tick re-runs the SAME key and never sets it. That is the whole
   * difference between "the window is moving under you" and "we checked again", and disabling the
   * window controls on the latter meant they went dead for the length of every poll — every 60s when
   * idle, every 10s while live.
   */
  isChangingWindow: boolean;
  error: Error | null;
  setPage: (page: number) => void;
  /** Clamps to the retention window AND resets to page 1. */
  setRange: (range: PastRunsRange) => void;
  refetch: () => void;
}

/**
 * How often to re-ask for this page of runs: the live cadence while something is running, the slow
 * one otherwise. It never returns `false` — a settled table keeps checking, just rarely.
 *
 * Exported so the rule can be pinned without driving a timer.
 */
export function pastRunsPollIntervalMs(runs: readonly RawSyncRun[] | undefined, live = false): number {
  // `live` is what lets the table DISCOVER a run, rather than only follow one it can already see.
  // Polling on the listed rows alone is not enough: the header's poll knows a run exists before this
  // table's page can, so without it a run started from the header showed up only a minute later, on
  // the settled tick. Verified against a live stack, which is the only way this shows up.
  if (live) return BASE_POLL_INTERVAL_MS;
  return runs?.some((run) => isRunning(run)) === true ? BASE_POLL_INTERVAL_MS : SETTLED_POLL_INTERVAL_MS;
}

/**
 * `live` says the sync has a run in flight per the header's own poll (`runSync.state`), which knows
 * before this table can: the header asks for the latest run unconditionally, this one asks for a page.
 *
 * `retentionDays` bounds the window this hook will ever ask for or accept via `setRange` — REQUIRED,
 * not defaulted: pass `retentionDays(sync)` (`lib/run-view.ts`), which is the one place a missing
 * value legitimately becomes the platform default. A default here would let a caller forget to pass
 * it and silently query the wrong window for a real connection.
 *
 * `live` carries no default, and cannot: it sits BEFORE a required parameter, so a caller can never
 * omit it and the default was unreachable code that read as an option. (`pastRunsPollIntervalMs`'s
 * own `live = false` is a different case — it is last there, and genuinely optional.)
 */
export function usePastRuns(syncId: string, live: boolean, retentionDays: number): PastRuns {
  const orpc = useOrpc();
  const [requestedPage, setRequestedPage] = useState(1);
  const [range, setRangeState] = useState<PastRunsRange>(() => defaultPastRunsRange(retentionDays));

  /**
   * Roll the default window when the day turns over.
   *
   * `range` is seeded once at mount and then only ever moved by the picker, so a page left open past
   * midnight keeps asking for a window that ends at the END OF YESTERDAY — and a run started after
   * midnight lands outside it. The poll cannot save it: it re-asks for the same stale window forever,
   * and the old Refresh button could not either, which is part of why it was not worth keeping.
   *
   * A single timer to the next midnight rather than a ticking clock: the only instant this can change
   * is that boundary. It re-arms off `range`, so each roll schedules the next. A machine asleep across
   * midnight fires late on wake, which is exactly when the correction is wanted.
   *
   * `pastRunsRangeIsStale` guards it so a deliberately narrowed window is never dragged forward.
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      setRangeState((current) =>
        pastRunsRangeIsStale(current, retentionDays) ? defaultPastRunsRange(retentionDays) : current,
      );
    }, msUntilNextLocalMidnight());
    return () => clearTimeout(timer);
  }, [range, retentionDays]);

  const query = useQuery({
    ...orpc.activations.listSyncRuns.queryOptions({
      input: {
        syncId,
        from: range.from.toISOString(),
        to: range.to.toISOString(),
        offset: (requestedPage - 1) * PAST_RUNS_PAGE_SIZE,
        limit: PAST_RUNS_PAGE_SIZE,
      },
    }),
    enabled: Boolean(syncId),
    placeholderData: keepPreviousData,
    /**
     * Poll only while a listed run is actually live, at the same cadence the header uses.
     *
     * This table used to have no `refetchInterval` at all, while three comments here and in
     * `RunsPanel` described the poll that keeps a live run's metrics moving. Two things followed.
     * A finished run kept its "Running…" badge and counted up forever, because nothing ever refetched
     * to tell it otherwise. And `RunDurationCell`'s `useTickingNow(isRunning(run))` reads THIS data
     * (the clock now lives per-row, in `RunsPanel`'s `RunDurationCell`, not as a single
     * `runs.some(isRunning)` gate over the whole table), so a still-live row's `active` never fell back
     * to `false` and its 1s interval never cleared — a page left open ticked for as long as it stayed
     * open. On the detail page the contradiction was visible in one screen: the header polls, so it
     * read Completed while the table underneath still counted.
     *
     * Reading the interval off the fetched data rather than off React state is what keeps the CADENCE
     * honest without a second source of truth: the first response in which nothing is running drops
     * this to `SETTLED_POLL_INTERVAL_MS`. It no longer stops — a settled table keeps checking, just
     * rarely, which is what replaced the manual Refresh control.
     */
    refetchInterval: (query) => pastRunsPollIntervalMs(query.state.data?.runs, live),
  });

  const totalRuns = query.data?.totalRuns ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalRuns / PAST_RUNS_PAGE_SIZE));
  // The result set can shrink under a reader — runs age out of the retention window, and a Refresh is
  // enough to land them on a page that no longer exists.
  //
  // The REQUEST has to come back, not just the number on screen. Clamping only the reported page
  // leaves the query asking for the old offset, so the table renders empty while `pageCount` falls
  // to 1 — and `RunsPanel` unmounts the pager at `pageCount > 1`, taking away the Previous
  // button that would have got the reader back. Adjusting during render re-runs this hook with the
  // corrected offset before anything paints, and converges because `pageCount` is at least 1.
  if (requestedPage > pageCount) {
    setRequestedPage(pageCount);
  }
  const page = Math.min(requestedPage, pageCount);

  function setRange(next: PastRunsRange): void {
    setRangeState(clampPastRunsRange(next, retentionDays));
    setRequestedPage(1);
  }

  function setPage(next: number): void {
    setRequestedPage(Math.min(Math.max(1, next), pageCount));
  }

  return {
    runs: query.data?.runs ?? [],
    totalRuns,
    page,
    pageCount,
    range,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isChangingWindow: query.isPlaceholderData,
    error: query.error,
    setPage,
    setRange,
    refetch: () => void query.refetch(),
  };
}
